import { fromByteArray } from 'base64-js';
import { XMLBuilder, XMLParser } from 'fast-xml-parser';
import md5 from 'md5';

import {
  GenericHttpError,
  type GenericHttpResponse,
} from '@iconik-io/core/src/http/generic_http_transport';
import { isString } from '@iconik-io/core/src/is_string';
import type { NonNullableBy, RequiredBy } from '@iconik-io/core/src/types';
import { AbortEndpointRequestError } from '@iconik-io/iconik-api/src/client/response';
import type {
  FilesMultiPartS3UrlPartSchema,
  FilesMultiPartUploadURLSchemaNullable,
} from '@iconik-io/iconik-api/src/services/files';
import { unwrapResponseBody } from '@iconik-io/iconik-api/src/utils/unwrap_response_body';

import {
  AbortUploadError,
  FileSizeError,
  InvalidFileObject,
  InvalidUploadObjectError,
  MultipartUploadChunkError,
  UninitializedChunkError,
} from '../../errors';
import {
  UPLOADER_CHUNK_STATUS_COMPLETED,
  UPLOADER_CHUNK_STATUS_FAILED,
  UPLOADER_CHUNK_STATUS_UPLOADING,
  UPLOADER_STATUS_COMPLETING,
} from '../../state';
import type { UploadEntity } from '../../types';
import { unwrapHeaderValue } from '../../utils/unwrap_header_value';
import { MultipartUploader } from '../multipart_uploader';
import {
  S3ChecksumsUnsupportedError,
  S3EmptyChunkEtagError,
  S3EmptyUploadIdError,
  S3InvalidCompleteResponseError,
  S3MultipartUploadIdEmptyError,
  S3UploadError,
} from './errors';

const MAX_PER_PAGE = 100;

const S3_REQUEST_TIMEOUT_ERROR_CODE = 'RequestTimeout';

/**
 * For more information about S3 multipart limitations:
 * https://docs.aws.amazon.com/AmazonS3/latest/dev/qfacts.html
 * Maximum object size: 5 TB
 * Maximum number of parts per upload: 10,000
 * Part numbers: 1 to 10,000 (inclusive)
 * Part size: 5 MB to 5 GB, last part can be < 5 MB
 * Maximum number of parts returned for a list parts request: 1000
 * Maximum number of multipart uploads returned in a list multipart uploads request: 1000
 */
const MAX_CHUNKS = 10_000;

const FILE_SIZE_LIMIT = 5_000_000_000_000;

const xmlParser = new XMLParser();

const xmlBuilder = new XMLBuilder({
  ignoreAttributes: false,
  format: true,
});

const xmlDeclaration = {
  '?xml': {
    '@_version': '1.0',
    '@_encoding': 'UTF-8',
    '@_standalone': 'yes',
  },
};

export type S3Error = {
  code: string;
  message: string;
};

const isStringOrBuffer = (body: unknown): body is string | Buffer =>
  isString(body) || body instanceof Buffer;

const parseS3ErrorResponse = (body: unknown): S3Error | undefined => {
  if (!isStringOrBuffer(body)) {
    return undefined;
  }

  const parser = new XMLParser({ ignoreAttributes: true });
  const parsed = parser.parse(body);
  const code = parsed.Error?.Code;
  const message = parsed.Error?.Message;

  if (!code || !message) {
    return undefined;
  }

  return { code, message };
};

const convertS3Error = (error: Error): Error => {
  if (!(error instanceof GenericHttpError)) {
    return error;
  }

  const s3ErrorInfo = parseS3ErrorResponse(error.response.body);
  if (s3ErrorInfo === undefined) {
    return error;
  }

  return new S3UploadError(s3ErrorInfo.code, s3ErrorInfo.message, error);
};

const shouldRetryHandler = (response: GenericHttpResponse): boolean => {
  if (response.status === 400) {
    const s3Error = parseS3ErrorResponse(response.body);
    if (s3Error?.code === S3_REQUEST_TIMEOUT_ERROR_CODE) {
      throw new S3UploadError(s3Error.code, s3Error.message);
    }
  }
  return false;
};

type UploadObject = NonNullableBy<
  RequiredBy<FilesMultiPartS3UrlPartSchema, 'url'>,
  'url'
>;

function assertUploadObject(
  urlObject: FilesMultiPartUploadURLSchemaNullable | undefined,
): asserts urlObject is UploadObject {
  if (!urlObject?.url) {
    throw new InvalidUploadObjectError(urlObject, 'URL is not set');
  }
}

type S3Meta = {
  uploadId?: string;
  uploadUrl: string;
};

type S3ChunkMeta = {
  etag: string;
};

export class S3Uploader extends MultipartUploader<S3Meta, S3ChunkMeta> {
  #uploadObjectPages = new Map<number, Promise<UploadObject[]>>();

  handleUploadEntityCreated({ entity }: { entity: UploadEntity }) {
    if (!entity.multipart_upload_url) {
      throw new InvalidFileObject(entity, 'Multipart upload URL is not set');
    }
    this.updateState({
      meta: {
        uploadUrl: entity.multipart_upload_url,
      },
    });
  }

  init() {
    this.on('uploadEntityCreated', this.handleUploadEntityCreated);
    super.init();
  }

  protected validate(): boolean {
    if (this.state.fileInfo.size > FILE_SIZE_LIMIT) {
      throw new FileSizeError(FILE_SIZE_LIMIT);
    }
    // Checksummed parts need the backend to sign each URL, which not every
    // entity family exposes. Reject up front rather than partway through.
    if (
      this.storageOptions.presign_md5_checksum &&
      !this.endpoints.s3ChecksummedUrlPart
    ) {
      throw new S3ChecksumsUnsupportedError();
    }
    return true;
  }

  async checkIfUploadExpired(
    assetId: string,
    fileId: string,
  ): Promise<boolean> {
    this.emit('log', {
      level: 'info',
      message: 'Checking if S3 upload expired',
      state: this.state,
    });

    const uploadId = this.state.meta?.uploadId;
    // If there is no upload id then the upload has not been started yet
    if (!uploadId) {
      return false;
    }

    const listPartUrlResponse = await unwrapResponseBody(
      this.apiClient.request(
        this.endpoints.multipartUrl(assetId, fileId, {
          upload_id: uploadId,
          type: 'list_parts_url',
        }),
        {
          abortSignal: this.abortList.newSignal(),
        },
      ),
    );
    const listPartUrl = listPartUrlResponse.list_parts_url?.at(0);
    if (!listPartUrl) {
      return false;
    }

    // Make a request to the list parts URL to check if the upload still exists
    const headers: Record<string, string> = { Accept: 'text/plain, */*' };
    if (this.storageOptions.userAgent) {
      headers['User-Agent'] = this.storageOptions.userAgent;
    }

    return this.httpClient
      .request(
        {
          method: 'get',
          url: listPartUrl,
          headers,
        },
        {
          abortSignal: this.abortList.newSignal(),
          retryOptions: {
            // Ensure that we don't retry on 404 which is the error we are checking below
            // We will still retry on timeouts and connection resets
            status: [],
          },
        },
      )
      .then(() => false)
      .catch((error) => {
        const s3Error = convertS3Error(error);
        if (
          s3Error instanceof S3UploadError &&
          s3Error.code === 'NoSuchUpload'
        ) {
          return true;
        }
        // Something else went wrong here so assume the upload still exists so that we don't accidentally start removing things
        // We will always be able to retry this restart the next time if it actually was removed.
        return false;
      });
  }

  #unwrapUploadId(): string {
    const meta = this.unwrapMeta();
    if (!meta.uploadId) {
      throw new S3EmptyUploadIdError('Upload ID is not set');
    }

    return meta.uploadId;
  }

  #getUploadObjectPage = (chunkIndex: number) => {
    const page = Math.floor(chunkIndex / MAX_PER_PAGE);
    const cachedPage = this.#uploadObjectPages.get(page);
    if (cachedPage !== undefined) {
      return cachedPage;
    }

    const assetId = this.unwrapAssetId();
    const entityId = this.unwrapUploadEntityId();
    const uploadId = this.#unwrapUploadId();
    const pagePromise = unwrapResponseBody(
      this.apiClient.request(
        this.endpoints.multipartUrlPart(assetId, entityId, {
          parts_num: this.unwrapChunks().length,
          per_page: MAX_PER_PAGE,
          page: page + 1,
          upload_id: uploadId,
        }),
        {
          abortSignal: this.abortList.newSignal(),
        },
      ),
    ).then(({ objects }) =>
      objects.map((object) => {
        assertUploadObject(object);
        return object;
      }),
    );
    this.#uploadObjectPages.set(page, pagePromise);
    return pagePromise;
  };

  #getUploadObject = async (
    chunkIndex: number,
    checksum: string | undefined,
  ) => {
    // If we don't need to specify a checksum per chunk we can be a bit more efficient and fetch
    // an entire page of upload objects at once
    if (checksum === undefined) {
      return this.#getUploadObjectPage(chunkIndex).then((objects) => {
        const indexInPage = chunkIndex % MAX_PER_PAGE;
        const uploadObject = objects[indexInPage];
        return uploadObject;
      });
    }

    // When we have a checksum we need to fetch the upload object for each chunk individually since the
    // backend needs that checksum to sign the URLs
    const s3ChecksummedUrlPart = this.endpoints.s3ChecksummedUrlPart;
    if (!s3ChecksummedUrlPart) {
      throw new S3ChecksumsUnsupportedError();
    }
    const assetId = this.unwrapAssetId();
    const entityId = this.unwrapUploadEntityId();
    const uploadId = this.#unwrapUploadId();
    return unwrapResponseBody(
      this.apiClient.request(
        s3ChecksummedUrlPart(assetId, entityId, {
          parts: [{ part_number: chunkIndex + 1, checksum }],
          upload_id: uploadId,
        }),
        { abortSignal: this.abortList.newSignal() },
      ),
    ).then((response) => {
      const uploadObject = response.parts.at(0);
      assertUploadObject(uploadObject);
      return uploadObject;
    });
  };

  async #uploadChunkIndex(chunkIndex: number): Promise<void> {
    const chunk = this.unwrapChunks()[chunkIndex];
    if (!chunk) {
      throw new UninitializedChunkError(chunkIndex);
    }

    this.updateChunks([
      { index: chunkIndex, status: UPLOADER_CHUNK_STATUS_UPLOADING },
    ]);

    try {
      const chunkData = await this.readChunkData(
        chunkIndex,
        this.abortList.newSignal(),
      );

      let checksum: string | undefined;
      if (this.storageOptions.presign_md5_checksum) {
        const md5Binary = md5(chunkData, { asBytes: true });
        checksum = fromByteArray(new Uint8Array(md5Binary));
        this.emit('log', {
          level: 'debug',
          message: `Chunk checksum: ${checksum}`,
          state: this.state,
        });
      }
      const uploadObject = await this.#getUploadObject(chunkIndex, checksum);

      const headers: Record<string, string> = {
        Accept: 'text/plain, */*',
      };
      if (this.storageOptions.userAgent) {
        headers['User-Agent'] = this.storageOptions.userAgent;
      }
      if (checksum) {
        headers['Content-MD5'] = checksum;
      }

      const response = await this.httpClient.request(
        {
          method: 'put',
          url: uploadObject.url,
          body: chunkData,
          headers,
        },
        {
          onUploadProgress: (event) =>
            this.uploadChunkProgress(chunkIndex, event),
          abortSignal: this.abortList.newSignal(),
          retryOptions: {
            shouldRetry: shouldRetryHandler,
          },
        },
      );

      const etag = unwrapHeaderValue(response, 'etag');
      this.updateChunks([
        {
          index: chunkIndex,
          meta: {
            etag,
          },
          status: UPLOADER_CHUNK_STATUS_COMPLETED,
        },
      ]);
    } catch (error) {
      if (
        error instanceof AbortUploadError ||
        error instanceof AbortEndpointRequestError
      ) {
        throw error;
      }
      this.updateChunks([
        { index: chunkIndex, status: UPLOADER_CHUNK_STATUS_FAILED },
      ]);
      if (!(error instanceof Error)) {
        throw error;
      }
      this.emit('log', {
        level: 'warn',
        message: `S3 chunk upload failed.`,
        error,
        state: this.state,
      });
      const s3Error = convertS3Error(error);
      throw new MultipartUploadChunkError(chunk, s3Error);
    }
  }

  async #getUploadId(multipartUploadUrl: string): Promise<string> {
    const headers: Record<string, string> = {
      Accept: 'text/plain, */*',
      'Content-Type': this.state.fileInfo.mimeType,
    };
    if (this.storageOptions.userAgent) {
      headers['User-Agent'] = this.storageOptions.userAgent;
    }

    const response = await this.httpClient
      .request(
        {
          method: 'post',
          url: multipartUploadUrl,
          headers,
        },
        {
          abortSignal: this.abortList.newSignal(),
          retryOptions: {
            shouldRetry: shouldRetryHandler,
            method: ['post'],
          },
        },
      )
      .catch((error) => {
        this.emit('log', {
          level: 'warn',
          message: `S3 Get upload id failed.`,
          error,
          state: this.state,
        });
        throw convertS3Error(error);
      });
    if (!isStringOrBuffer(response.body)) {
      throw new S3MultipartUploadIdEmptyError();
    }
    const parsedResponse = xmlParser.parse(response.body);

    const uploadId = parsedResponse.InitiateMultipartUploadResult?.UploadId;
    if (!uploadId) {
      throw new S3MultipartUploadIdEmptyError();
    }

    return uploadId;
  }

  protected async runUpload(): Promise<void> {
    const meta = this.unwrapMeta();
    if (!meta.uploadId) {
      const uploadId = await this.#getUploadId(meta.uploadUrl);
      this.updateState({ meta: { ...meta, uploadId } });
    }

    this.#uploadObjectPages.clear();

    await this.chunksQueue.addAll(
      this.getIncompleteChunks().map(
        (chunk) => () => this.#uploadChunkIndex(chunk.index),
      ),
    );
    await this.#complete();
  }

  async #complete(): Promise<void> {
    this.updateState({ status: UPLOADER_STATUS_COMPLETING });

    const assetId = this.unwrapAssetId();
    const entityId = this.unwrapUploadEntityId();
    const uploadId = this.#unwrapUploadId();

    const completeUrlResponse = await this.apiClient.request(
      this.endpoints.multipartUrl(assetId, entityId, {
        upload_id: uploadId,
        type: 'complete_url',
      }),
      {
        abortSignal: this.abortList.newSignal(),
      },
    );
    const completeUrl = completeUrlResponse.body.complete_url;
    if (!completeUrl) {
      throw new S3InvalidCompleteResponseError('Complete URL is empty');
    }

    const xmlObject = {
      ...xmlDeclaration,
      CompleteMultipartUpload: {
        Part: this.unwrapChunks().map((chunk) => {
          const etag = chunk.meta?.etag;
          if (!etag) {
            throw new S3EmptyChunkEtagError(chunk);
          }
          return {
            ETag: etag,
            PartNumber: chunk.index + 1,
          };
        }),
      },
    };

    const body = xmlBuilder.build(xmlObject);

    const headers: Record<string, string> = {
      'Content-Type': 'text/plain',
      Accept: 'text/plain, */*',
    };
    if (this.storageOptions.userAgent) {
      headers['User-Agent'] = this.storageOptions.userAgent;
    }

    await this.httpClient
      .request(
        {
          method: 'post',
          url: completeUrl,
          body,
          headers,
        },
        {
          abortSignal: this.abortList.newSignal(),
          retryOptions: {
            shouldRetry: shouldRetryHandler,
            method: ['post'],
          },
        },
      )
      .then(() =>
        this.apiClient.request(
          this.endpoints.multipartCleanup(assetId, entityId, {
            upload_id: uploadId,
            abort_upload: false,
          }),
          { abortSignal: this.abortList.newSignal() },
        ),
      )
      .catch((error) => {
        this.emit('log', {
          level: 'warn',
          message: `S3 complete failed. ${completeUrl}`,
          error,
          state: this.state,
        });
        throw convertS3Error(error);
      });
  }

  protected getChunkSize() {
    let chunkSize = super.getChunkSize();
    const fileSize = this.state.fileInfo.size;

    // If chunks count is greater than MAX_CHUNKS, alter chunk size to fit in MAX_CHUNKS
    const chunksCount = Math.ceil(fileSize / chunkSize);
    if (chunksCount > MAX_CHUNKS) {
      chunkSize = Math.ceil(fileSize / MAX_CHUNKS);
    }

    return chunkSize;
  }

  protected async runCancel(): Promise<void> {}
}
